import './styles.css';
import { createRenderer, renderParams, type HorizonMode } from './render/scene';
import { LandscapeFrame } from './ui/landscapeFrame';
import type { Mesh } from './render/babylon';
import { buildGreyboxTrack } from './render/greybox';
import { buildTrackMeshes } from './render/trackMesh';
import { buildBrakingBoards } from './render/brakingBoards';
import { buildSepangScenery, sceneryParams } from './render/scenery';
import { Quality, QUALITY_LABELS, guessLevel } from './render/quality';
import { createWeatherVisuals } from './render/weather';
import { buildTerrain } from './render/terrain';
import { buildFences } from './render/fences';
import { buildPitLane } from './render/pitLane';
import { brakingBoards } from './track/brakingBoards';
import { makeOval } from './track/track';
import { sepang } from './track/circuits';
import { SEPANG_RACELINE } from './track/data/sepang';
import { Session, simpleSurface, type TrackLayout } from './sim/session';
import { FixedStep } from './sim/loop';
import { LapRecorder } from './sim/lapRecorder';
import { OrientationSensor, requestMotionPermission } from './input/sensors';
import { SteeringProcessor, steeringParams } from './input/steering';
import { KeyboardControls, TouchPedals } from './input/controls';
import { addTapHaptic, hapticsEnabled, KerbRumble, setHapticsEnabled, vibrate } from './input/haptics';
import { ControllerManager, controllerParams, type ControllerAction } from './input/gamepad';
import { Hud, formatTime } from './ui/hud';
import { CREDITS_HTML, NO_SENSOR_HTML, Overlays, PERMISSION_DENIED_HTML } from './ui/overlays';
import { PauseMenu } from './ui/menu';
import { SettingsPage } from './ui/settings';
import { setParam, type Param } from './config/params';
import { Onboarding, hasOnboarded, resetOnboarding } from './ui/onboarding';
import { shareLap } from './ui/shareCard';
import { loadBest, saveBest, type StoredLap } from './storage/pbStore';
import { PHYSICS_VERSION } from './sim/version';
import { CarAudio, MguMeter, audioParams, configureAudioSession, fillAudioState, idleAudioState } from './audio';
import { DevPanel } from './dev/panel';
import { Race, type RaceConfig } from './race/race';
import { medalFor, type Challenge } from './race/challenges';
import { applySetup, loadSetup, saveSetup, wingLabel } from './race/setup';
import { WEATHER_LABELS } from './race/weather';
import { NO_AIDS, neutralMods, sameAids } from './sim/car';
import { RaceHud } from './ui/raceHud';
import { COMPOUNDS } from './race/tyres';
import { radioBeep } from './audio/radioBeep';
import { RainAudio } from './audio/weatherAudio';
import { PitMenu } from './ui/pitMenu';
import { ResultsScreen } from './ui/results';
import { buildChallenges, buildRaceSetup, toConfig } from './ui/raceScreens';
import { Telemetry } from './dev/telemetry';
import { WheelManager, installFakeWheel } from './wheel/wheelManager';
import { WheelSetup } from './ui/wheelSetup';
import { WheelMenuNav } from './ui/wheelNav';
import { AutoDrive } from './dev/autodrive';
import { InputOverlay } from './dev/inputOverlay';
import { gearboxMode, simAids } from './wheel/settings';
import type { Action } from './wheel/bindings';
import type { Surface } from './sim/session';

const PHYSICS_HZ = 240;

// Offline support for the installed app (production builds only).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Without a service worker the game still works online.
    });
  });
}
const dev = new URLSearchParams(location.search).has('dev');

// Created before the renderer so the game container is sized (and rotated if
// iOS has the page in portrait) before the engine measures the canvas.
const frame = new LandscapeFrame(document.getElementById('app') as HTMLElement);
const canvas = document.getElementById('game') as HTMLCanvasElement;
const renderer = createRenderer(canvas);
frame.onChange = () => renderer.engine.resize();
// Desktops (mouse/wheel, no touch) are offered Ultra; Auto starts there only
// with a big screen and a discrete-class GPU (see quality.ts).
const desktop = matchMedia('(pointer: fine)').matches && !matchMedia('(pointer: coarse)').matches;
const dpr = window.devicePixelRatio || 1;
const quality = new Quality(
  guessLevel(navigator, {
    finePointer: desktop,
    screenPixels: { width: screen.width * dpr, height: screen.height * dpr },
    gpu: renderer.engine.getGlInfo().renderer ?? '',
  }),
  desktop,
);
renderer.setQuality(quality.profile);

let scenery: Mesh[] = [];
let buildScenery: (() => void) | undefined;

// Sepang by default; ?track=oval loads the grey-box test circuit.
const useOval = new URLSearchParams(location.search).get('track') === 'oval';
const layout: TrackLayout = useOval ? ovalLayout() : sepang();
const track = layout.track;
if (useOval) buildGreyboxTrack(renderer.scene, track, 25);
else {
  const boards = brakingBoards(track);
  // Braking zones for the rubber marks: from the 150 m point to the corner.
  const brakingZones = boards.filter((b) => b.distance === 100).map((b): [number, number] => [(b.s - 50 + track.length) % track.length, (b.s + 100) % track.length]);
  buildTrackMeshes(renderer.scene, track, { raceline: SEPANG_RACELINE, brakingZones });
  buildBrakingBoards(renderer.scene, boards);
  buildFences(renderer.scene, track);
  // After the fences: it cuts the barrier and catch fence where the pit roads cross them.
  if (layout.pitLane) buildPitLane(renderer.scene, track, layout.pitLane);
  const terrain = buildTerrain(renderer.scene, track);
  buildScenery = () => {
    disposeScenery(scenery);
    // Tree density follows the quality level (the dev slider sets the High value).
    const base = sceneryParams.trees.value;
    sceneryParams.trees.value = base * quality.profile.trees;
    try {
      scenery = buildSepangScenery(renderer.scene, track, terrain.groundAt, terrain.floor);
    } finally {
      sceneryParams.trees.value = base;
    }
  };
  buildScenery();
}
// Rain, wet track and spray (races only; time trial is always dry).
const weatherFx = createWeatherVisuals(renderer.scene, renderer.camera, { quality: () => (quality.level === 'ultra' ? 'high' : quality.level) });
quality.onChange = () => {
  renderer.setQuality(quality.profile);
  buildScenery?.();
};

/** Dispose scenery meshes and materials, and any texture no remaining material uses (some are shared with the track). */
function disposeScenery(meshes: Mesh[]): void {
  const scene = renderer.scene;
  const materials = new Set(meshes.flatMap((m) => [m, ...m.getChildMeshes()]).map((m) => m.material).filter((m) => m !== null));
  for (const m of meshes) m.dispose(false, false);
  const textures = new Set([...materials].flatMap((m) => m.getActiveTextures()));
  for (const m of materials) m.dispose(false, false);
  for (const t of textures) if (!scene.materials.some((m) => m.hasTexture(t))) t.dispose();
}

function ovalLayout(): TrackLayout {
  const oval = makeOval();
  const L = oval.length;
  const arc = Math.PI * 120;
  return {
    track: oval,
    surfaceAt: simpleSurface,
    barrierOffset: (p) => ({ right: p.widthRight + 25, left: p.widthLeft + 25 }),
    aeroZones: [
      [60, 560],
      [600 + arc + 60, 600 + arc + 560],
    ],
    sectorEnds: [L / 3, (2 * L) / 3],
    // Start the flying lap on the back straight, clear of the bend.
    flyingStartDistance: arc + 100,
  };
}

const trackId = useOval ? 'oval' : 'sepang';
const trackName = useOval ? 'Test oval' : 'Sepang';
// Wheel (sim mode) laps are a different discipline from tilt laps: separate bests.
const modeSuffix = (sim: boolean): string => (sim ? ':wheel' : '');
let pbKey = `${trackId}:v${PHYSICS_VERSION}`;

const session = new Session(layout);
const recorder = new LapRecorder();
let bestStored: StoredLap | undefined;
function loadTrialBest(): void {
  const key = pbKey;
  void loadBest(key).then((best) => {
    if (key !== pbKey) return;
    if (!best || (session.bestLap !== undefined && session.bestLap < best.lapTime)) return;
    bestStored = best;
    recorder.setBest(best.samples, best.lapTime);
    session.setBest(best.lapTime, best.sectors);
    overlays.setBest(`Your best${session.car.simMode ? ' (wheel)' : ''}: ${formatTime(best.lapTime)}`);
  });
}
loadTrialBest();
const fixed = new FixedStep(1 / PHYSICS_HZ);

// Game mode: time trial (v1) or a race against the clock (v2).
let race: Race | undefined;
let raceChallenge: Challenge | undefined;
let raceConfig: RaceConfig | undefined;
let raceKey = '';
let raceFinishedAt = 0;
const setup = loadSetup();
// The current race's path (for the next best-race ghost) and the best race so far.
const raceRecorder = new LapRecorder(5);
const raceBest = new LapRecorder(5);
let raceBestTotal: number | undefined;

/** Time trial: neutral car plus the player's setup. */
function trialMods(): void {
  session.car.mods = neutralMods();
  applySetup(session.car.mods, setup);
}
trialMods();

const sensor = new OrientationSensor();
sensor.angle = () => frame.gameAngle;
let landscapeChosen = false;
const steering = new SteeringProcessor();
const keyboard = new KeyboardControls();
const pedals = new TouchPedals(document.getElementById('touch') as HTMLElement, (e) => ({
  x: frame.toGame(e.clientX, e.clientY).x,
  width: frame.width,
}));
const kerbRumble = new KerbRumble();

// Steering wheel: WebHID (Logitech, force feedback) or Gamepad API (input only).
// ?fakewheel installs a simulated G27 (?fakewheel=g29 a G29) for development without the hardware.
const fakeWheelParam = new URLSearchParams(location.search).get('fakewheel');
const fakeWheel = fakeWheelParam !== null ? installFakeWheel(fakeWheelParam === 'g29' ? 'g29' : 'g27') : undefined;
if (fakeWheel) (window as unknown as { fakeWheel: typeof fakeWheel }).fakeWheel = fakeWheel;
const wheel = new WheelManager();
(window as unknown as { wheel: WheelManager }).wheel = wheel;
// Game controllers (Xbox, PlayStation, others): Gamepad API, any browser and platform. They drive the
// assisted car like tilt and the keyboard; a connected wheel takes over the steering and pedals.
const controller = new ControllerManager();
controller.init();
// Demo/recording tools: ?autodrive (scripted driver on the simulated wheel), ?overlay (driver inputs
// panel), ?capture (fixed 1/30 s frames stepped by window.renderFrame(), for frame-exact video).
const urlFlags = new URLSearchParams(location.search);
const autoDrive = fakeWheel && urlFlags.has('autodrive') ? new AutoDrive(SEPANG_RACELINE) : undefined;
if (autoDrive && fakeWheel) fakeWheel.motor = false; // the script holds the wheel
const inputOverlay = urlFlags.has('overlay') ? new InputOverlay(document.getElementById('dev') as HTMLElement, wheel) : undefined;
const captureDt = urlFlags.has('capture') ? 1 / Number(urlFlags.get('capture') || 30) : 0;
(window as unknown as { game: unknown }).game = {
  session,
  // For scripts and the end-to-end checks (scripts/e2e.ts).
  get race() {
    return race;
  },
};

const hud = new Hud(document.getElementById('hud') as HTMLElement, track, layout.sectorEnds.length + 1);
const overlays = new Overlays(document.getElementById('overlays') as HTMLElement);

let horizon: HorizonMode = loadHorizon();
let ghostOn = loadFlag('fone.ghost', true);
setHapticsEnabled(loadFlag('fone.haptics', true));
// The old sound on/off switch became the volume setting (0 = off).
try {
  if (localStorage.getItem('fone.sound') === '0') setParam(audioParams.master.id, 0);
  localStorage.removeItem('fone.sound');
} catch {
  // Nothing to migrate.
}
let running = false;
let paused = false;
let recentred = false;

function loadFlag(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}

function saveFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Not persisted; fine.
  }
}

const onboarding = new Onboarding(document.getElementById('hud') as HTMLElement);
const menu = new PauseMenu(document.getElementById('overlays') as HTMLElement, [
  { id: 'resume', label: () => 'Resume', run: () => undefined, closes: true },
  {
    id: 'restart',
    label: () => (race ? 'Restart race' : 'Restart lap'),
    run: () => {
      if (race && raceConfig) {
        startRace(raceConfig, raceChallenge);
        return;
      }
      session.flyingStart();
      recorder.startLap();
    },
    closes: true,
  },
  {
    id: 'settings',
    label: () => 'Settings',
    run: () => settings.show(),
  },
  {
    id: 'wheel',
    label: () => (wheel.active ? `Steering wheel: ${wheel.source!.name}` : 'Steering wheel'),
    run: () => openWheelSetup(true),
  },
  {
    id: 'setup',
    label: () => `Car setup: ${wingLabel(setup.wing)} wing`,
    run: () => carSetup.show(),
  },
  {
    id: 'share',
    label: () => (bestStored ? `Share best lap (${formatTime(bestStored.lapTime)})` : 'Share best lap (set a lap first)'),
    run: () => {
      if (bestStored) void shareLap(track, { trackName, lapTime: bestStored.lapTime, sectors: bestStored.sectors, date: new Date(bestStored.date) });
    },
  },
  {
    id: 'credits',
    label: () => 'Credits',
    run: () => overlays.showMessage(CREDITS_HTML),
    closes: true,
  },
  {
    id: 'tutorial',
    label: () => 'Replay tutorial',
    run: () => {
      resetOnboarding();
      onboarding.start();
    },
    closes: true,
  },
  {
    id: 'quit',
    label: () => (race ? 'Retire to menu' : 'Main menu'),
    run: () => quitToMenu(),
    closes: true,
  },
]);
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const nudge = (p: Param, delta: number, lo = p.min, hi = p.max): void => setParam(p.id, Math.round(clamp(p.value + delta, lo, hi) * 1000) / 1000);
const VOLUME_FULL = audioParams.master.defaultValue;
const settings = new SettingsPage(
  document.getElementById('overlays') as HTMLElement,
  [
    {
      id: 'range',
      label: 'Tilt for full lock',
      hint: 'Lower is more sensitive',
      value: () => `${steeringParams.range.value}°`,
      dec: () => nudge(steeringParams.range, -5, 30, 90),
      inc: () => nudge(steeringParams.range, 5, 30, 90),
    },
    {
      id: 'deadZone',
      label: 'Centre dead zone',
      hint: 'Ignores small wobbles',
      value: () => (steeringParams.deadZone.value <= 0 ? 'Off' : `${(steeringParams.deadZone.value * steeringParams.range.value).toFixed(1)}°`),
      dec: () => nudge(steeringParams.deadZone, -0.01, 0, 0.1),
      inc: () => nudge(steeringParams.deadZone, 0.01, 0, 0.1),
    },
    {
      id: 'curve',
      label: 'Steering response',
      hint: 'Higher is finer near centre',
      value: () => (steeringParams.curve.value <= 1 ? 'Linear' : steeringParams.curve.value.toFixed(1)),
      dec: () => nudge(steeringParams.curve, -0.1, 1, 2),
      inc: () => nudge(steeringParams.curve, 0.1, 1, 2),
    },
    {
      id: 'padDeadZone',
      label: 'Stick dead zone',
      hint: 'Raise if the car pulls with the stick centred',
      value: () => (controllerParams.deadZone.value <= 0 ? 'Off' : `${Math.round(controllerParams.deadZone.value * 100)}%`),
      dec: () => nudge(controllerParams.deadZone, -0.02, 0, 0.3),
      inc: () => nudge(controllerParams.deadZone, 0.02, 0, 0.3),
      visible: () => controller.connected,
    },
    {
      id: 'padCurve',
      label: 'Stick response',
      hint: 'Higher is finer near centre',
      value: () => (controllerParams.curve.value <= 1 ? 'Linear' : controllerParams.curve.value.toFixed(1)),
      dec: () => nudge(controllerParams.curve, -0.1, 1, 3),
      inc: () => nudge(controllerParams.curve, 0.1, 1, 3),
      visible: () => controller.connected,
    },
    {
      id: 'horizon',
      label: 'Horizon',
      value: () => (horizon === 'screen' ? 'Locked to screen' : 'Level with world'),
      cycle: () => toggleHorizon(),
    },
    {
      id: 'volume',
      label: 'Volume',
      value: () => (audioParams.master.value <= 0 ? 'Off' : `${Math.round((audioParams.master.value / VOLUME_FULL) * 100)}%`),
      dec: () => nudge(audioParams.master, -VOLUME_FULL / 10, 0, VOLUME_FULL * 1.5),
      inc: () => nudge(audioParams.master, VOLUME_FULL / 10, 0, VOLUME_FULL * 1.5),
    },
    {
      id: 'ghost',
      label: 'Ghost car',
      hint: 'Your best lap',
      value: () => (ghostOn ? 'On' : 'Off'),
      cycle: () => {
        ghostOn = !ghostOn;
        saveFlag('fone.ghost', ghostOn);
      },
    },
    {
      id: 'quality',
      label: 'Graphics',
      hint: 'Lower if it stutters or gets hot',
      value: () => (quality.choice === 'auto' ? `Auto · ${QUALITY_LABELS[quality.level]}` : QUALITY_LABELS[quality.choice]),
      cycle: () => quality.cycle(),
    },
    // Desktops: a big monitor at a desk wants a narrower view than a phone held
    // at arm's length (about 34° is true to life on a 32" screen at 65 cm).
    ...(desktop
      ? [
          {
            id: 'fov',
            label: 'Field of view',
            hint: 'Lower suits a big screen',
            value: () => `${renderParams.fovDeg.value}°`,
            dec: () => nudge(renderParams.fovDeg, -2, 30, 70),
            inc: () => nudge(renderParams.fovDeg, 2, 30, 70),
          },
        ]
      : []),
    {
      id: 'haptics',
      label: 'Vibration',
      hint: 'Taps and kerbs',
      value: () => (hapticsEnabled() ? 'On' : 'Off'),
      cycle: () => {
        setHapticsEnabled(!hapticsEnabled());
        saveFlag('fone.haptics', hapticsEnabled());
      },
    },
  ],
  [
    {
      label: 'Reset steering',
      kind: 'secondary',
      run: () => {
        for (const p of [steeringParams.range, steeringParams.deadZone, steeringParams.curve, controllerParams.deadZone, controllerParams.curve]) setParam(p.id, p.defaultValue);
      },
    },
    { label: 'Done', kind: 'primary', run: () => undefined, closes: true },
  ],
);
settings.onClose = () => menu.show();

const wheelSetup = new WheelSetup(document.getElementById('overlays') as HTMLElement, wheel);
const wheelNav = new WheelMenuNav(document.getElementById('overlays') as HTMLElement);
let wheelSetupFromMenu = false;
wheelSetup.onClose = () => {
  updateStartWheel();
  if (wheelSetupFromMenu) menu.show();
  else overlays.showStart(true);
};
function openWheelSetup(fromMenu: boolean): void {
  wheelSetupFromMenu = fromMenu;
  if (!fromMenu) overlays.showStart(false);
  wheelSetup.show();
}
function updateStartWheel(): void {
  const s = wheel.status;
  const pad = controller.labels;
  let text = '';
  if (s === 'hid' && wheel.hid!.ps4Mode) text = 'Logitech G29 found with its switch on PS4: slide it to PS3 and reconnect';
  else if (s === 'hid') text = `${wheel.source!.name} ready${wheel.hasFfb ? ' · force feedback on' : ''} · paddles move, OK button selects`;
  else if (s === 'gamepad') text = `${wheel.source!.name} ready · no force feedback in this mode`;
  else if (controller.connected) text = `${controller.name} ready · D-pad moves, ${pad.select} selects, ${pad.back} goes back`;
  overlays.setWheel(text);
}
const prevWheelChange = wheel.onChange;
let wheelWasActive = false;
wheel.onChange = () => {
  prevWheelChange?.();
  updateStartWheel();
  // Unplugged mid-drive: pause rather than hand the car to tilt/keyboard mid-corner.
  if (wheelWasActive && !wheel.active) pauseForInterruption();
  wheelWasActive = wheel.active;
};
void wheel.init().then(updateStartWheel);
let padWasConnected = false;
controller.onChange = () => {
  updateStartWheel();
  // Controller lost mid-drive (battery, Bluetooth): pause rather than hand the car to tilt/keyboard.
  if (padWasConnected && !controller.connected && !wheel.active) pauseForInterruption();
  padWasConnected = controller.connected;
};
overlays.onWheel(() => openWheelSetup(false));

const onSetupChange = (): void => {
  saveSetup(setup);
  if (!race) trialMods();
};
const carSetup = new SettingsPage(
  document.getElementById('overlays') as HTMLElement,
  [
    {
      id: 'wing',
      label: 'Wing level',
      hint: 'More grip vs top speed',
      value: () => wingLabel(setup.wing),
      dec: () => ((setup.wing = Math.max(-2, setup.wing - 1)), onSetupChange()),
      inc: () => ((setup.wing = Math.min(2, setup.wing + 1)), onSetupChange()),
    },
    {
      id: 'bias',
      label: 'Brake bias',
      hint: 'Front share; lower rotates more',
      value: () => `${Math.round(setup.bias * 100)}%`,
      dec: () => ((setup.bias = Math.max(0.5, Math.round((setup.bias - 0.01) * 100) / 100)), onSetupChange()),
      inc: () => ((setup.bias = Math.min(0.62, Math.round((setup.bias + 0.01) * 100) / 100)), onSetupChange()),
    },
  ],
  [{ label: 'Done', kind: 'primary', run: () => undefined, closes: true }],
  'Car setup',
);
carSetup.onClose = () => menu.show();

const overlayRoot = document.getElementById('overlays') as HTMLElement;
const raceHud = new RaceHud(document.getElementById('hud') as HTMLElement, hud.mapElement);
raceHud.onBox = () => race?.toggleBox();
raceHud.onRadio = (m) => {
  if (audioCtx) radioBeep(audioCtx, m.kind === 'box' || m.kind === 'flag');
};
const pitMenu = new PitMenu(document.getElementById('hud') as HTMLElement);
const results = new ResultsScreen(overlayRoot);
const raceSetup = buildRaceSetup(
  overlayRoot,
  setup,
  onSetupChange,
  (choices) => {
    raceSetup.hide();
    void enterGame().then((ok) => {
      if (ok) startRace(toConfig(choices, setup));
      else overlays.showStart(true);
    });
  },
  () => overlays.showStart(true),
);
const challengeBest = (c: Challenge): { medal?: string; total?: number } => {
  try {
    const raw = localStorage.getItem(`fone.challenge.${c.id}`);
    if (!raw) return {};
    const total = Number(raw);
    return { total, medal: medalFor(c, total) };
  } catch {
    return {};
  }
};
const challenges = buildChallenges(
  overlayRoot,
  challengeBest,
  (c) => {
    challenges.hide();
    void enterGame().then((ok) => {
      if (ok) startRace({ ...c.config, setup: { ...setup } }, c);
      else overlays.showStart(true);
    });
  },
  () => overlays.showStart(true),
);
overlays.onRace(() => {
  overlays.showStart(false);
  raceSetup.show();
});
overlays.onChallenges(() => {
  overlays.showStart(false);
  challenges.show();
});
results.onAgain = () => {
  if (raceConfig) startRace({ ...raceConfig, seed: raceChallenge ? raceConfig.seed : Math.floor(Math.random() * 1e6) }, raceChallenge);
};
results.onSetup = () => {
  running = false;
  endRace();
  if (raceChallenge) challenges.show();
  else raceSetup.show();
};
results.onMenu = () => quitToMenu();

let pitMenuShown = false;
/** Pit menu: open while the car drives itself in, locked at the stop, gone on the way out. */
function updatePitMenu(r: Race): void {
  const stop = r.phase === 'pit' ? r.pitStop : undefined;
  const choosing = stop && (stop.stage === 'entry' || stop.stage === 'limiter');
  if (stop && (choosing || stop.stage === 'stationary')) {
    if (!pitMenuShown) {
      pitMenuShown = true;
      const spec = COMPOUNDS[r.pitChoice];
      pitMenu.show(r.pitChoice, `Engineer: ${spec.name}${r.damage.needsNose ? ' · new nose' : ''}`);
    }
    if (choosing) r.pitChoice = pitMenu.choice;
    else {
      if (!pitMenu.locked) pitMenu.lock();
      pitMenu.setNote(`Stationary ${Math.max(0, stop.stationaryLeft).toFixed(1)} s`);
    }
  } else if (pitMenuShown) {
    pitMenuShown = false;
    pitMenu.hide();
  }
}

function updateRaceHud(r: Race): void {
  updatePitMenu(r);
  const spec = r.tyres.spec;
  const wearPerLap = r.tyres.laps > 0 ? r.tyres.wear / r.tyres.laps : r.scale / spec.cliffLaps;
  const ghostT = raceBest.bestTimeAt(r.progress * track.length);
  raceHud.update({
    lightsLit: r.lightsLit,
    showLights: r.phase === 'lights' || r.phase === 'grid',
    lap: r.lap,
    laps: r.config.laps,
    raceTime: r.raceTime,
    penalties: r.penalties,
    compound: r.tyres.compound,
    wear: r.tyres.wear,
    tyreLapsLeft: wearPerLap > 0 ? Math.max(0, (1 - r.tyres.wear) / wearPerLap) : 99,
    damage: Math.max(r.damage.frontWing, r.damage.floor),
    rain: r.weather.rain,
    wetness: r.weather.wetness,
    lowGrip: r.weather.lowGrip,
    forecast: r.weather.forecast(r.progress, 5, r.config.seed),
    ghostGap: ghostT !== undefined && r.phase === 'racing' && r.progress > 0.05 ? r.raceTime - ghostT : undefined,
    pitHint: r.pitHint(),
    pitLoss: 24,
    ruleMet: r.compoundRuleMet,
    pitLimiter: !!r.pitStop?.limiterOn,
  });
}

function startRace(config: RaceConfig, challenge?: Challenge): void {
  raceConfig = config;
  raceChallenge = challenge;
  syncDriveMode();
  race = new Race(session, config);
  raceKey =
    (challenge ? `challenge:${challenge.id}:v${PHYSICS_VERSION}` : `race:${trackId}:${config.laps}:${config.plan ? 'plan' : config.weather}:v${PHYSICS_VERSION}`) +
    modeSuffix(session.car.simMode);
  raceRecorder.startLap();
  raceBest.best = undefined;
  raceBest.bestTime = undefined;
  raceBestTotal = undefined;
  const key = raceKey;
  void loadBest(key).then((best) => {
    if (!best || key !== raceKey) return;
    raceBest.setBest(best.samples, best.lapTime);
    raceBestTotal = best.lapTime;
  });
  raceFinishedAt = 0;
  results.hide();
  pitMenu.hide();
  pitMenuShown = false;
  raceHud.show(true);
  onboarding.stop();
  running = true;
  menu.close();
  const title = challenge ? challenge.name : `${config.laps} laps · ${WEATHER_LABELS[config.weather]}`;
  hud.showNotice(title, 3);
}

/** Leave race mode and put the car back into time-trial trim. */
function endRace(): void {
  race = undefined;
  session.envHook = undefined;
  session.car.pu.harvestLimitMJ = undefined;
  trialMods();
  raceHud.show(false);
  pitMenu.hide();
}

function quitToMenu(): void {
  running = false;
  endRace();
  results.hide();
  menu.close();
  overlays.showStart(true);
}

function finishRace(r: Race): void {
  const res = r.result;
  if (!res) return;
  const previous = raceBestTotal;
  const medal = raceChallenge ? medalFor(raceChallenge, res.total) : undefined;
  if (previous === undefined || res.total < previous) {
    const samples = raceRecorder.lastLap.length ? raceRecorder.lastLap : [];
    void saveBest(raceKey, { lapTime: res.total, sectors: [], samples, date: new Date().toISOString() });
    if (raceChallenge) {
      try {
        localStorage.setItem(`fone.challenge.${raceChallenge.id}`, String(res.total));
      } catch {
        // Not persisted.
      }
    }
  }
  const cfg = r.config;
  results.show(res, {
    title: raceChallenge ? raceChallenge.name : 'Race complete',
    subtitle: raceChallenge ? raceChallenge.description : `${cfg.laps} laps · ${WEATHER_LABELS[cfg.weather]} · Sepang`,
    previousBest: previous,
    medal,
    targets: raceChallenge?.medals,
  });
}

hud.timingPanel.addEventListener('click', () => {
  if (running) menu.show();
});

function loadHorizon(): HorizonMode {
  try {
    // Screen-locked is the default (preferred in the first device playtest).
    return localStorage.getItem('fone.horizon') === 'world' ? 'world' : 'screen';
  } catch {
    return 'screen';
  }
}

function toggleHorizon(): void {
  horizon = horizon === 'world' ? 'screen' : 'world';
  try {
    localStorage.setItem('fone.horizon', horizon);
  } catch {
    // Not persisted; fine.
  }
  hud.showNotice(horizon === 'world' ? 'Horizon: level with the world (A)' : 'Horizon: locked to screen (B)');
}

function recentre(): void {
  steering.recentre();
  recentred = true;
  hud.showNotice('Steering re-centred', 1);
}

hud.recentreButton.addEventListener('click', recentre);
addTapHaptic(hud.recentreButton);
keyboard.onRecentre = recentre;
keyboard.onToggleHorizon = toggleHorizon;
if (dev) {
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyF') race?.forceFinish();
    if (e.code === 'KeyJ') race?.forcePit();
  });
}
keyboard.onBox = () => race?.toggleBox();
keyboard.onShiftUp = () => wheelAction('upshift');
keyboard.onShiftDown = () => wheelAction('downshift');

/**
 * A wheel connected means sim mode: direct steering, real brakes and traction,
 * the chosen gearbox, gentler auto-reset. Re-checked every frame so plugging in
 * or changing a setting takes effect straight away.
 */
function syncDriveMode(): void {
  const car = session.car;
  const sim = wheel.active;
  const aids = sim ? simAids() : NO_AIDS;
  if (car.simMode !== sim) session.invalidateLap(); // a lap driven partly in each mode counts for neither
  if (car.simMode !== sim || !sameAids(car.aids, aids)) car.setSimMode(sim, aids);
  car.gearbox.setMode(sim ? gearboxMode() : 'auto', car.vx);
  // H-shifter without a clutch pedal bound: gears go straight in.
  car.gearbox.requireClutch = !!wheel.profile.clutch;
  session.lenientReset = sim;
  const key = `${trackId}:v${PHYSICS_VERSION}${modeSuffix(sim)}`;
  if (key !== pbKey) {
    pbKey = key;
    bestStored = undefined;
    recorder.best = undefined;
    recorder.bestTime = undefined;
    session.bestLap = undefined;
    session.bestSectors = [];
    overlays.setBest('');
    loadTrialBest();
  }
}

/** Wheel buttons (and E/Q on the keyboard in wheel mode). */
function wheelAction(a: Action): void {
  const car = session.car;
  const driving = running && !paused && !menu.open && !settings.open && !carSetup.open && !results.open && !wheelSetup.open;
  if (!driving) {
    menuAction(a);
    return;
  }
  if (a === 'pause') {
    keyboard.onPause?.();
    return;
  }
  // Pit lane: the car drives itself, so the paddles pick the tyres.
  if (pitMenu.shown && (a === 'upshift' || a === 'downshift')) {
    pitMenu.cycle(a === 'upshift' ? 1 : -1);
    return;
  }
  if (a === 'upshift' && car.gearbox.mode === 'sequential') car.gearbox.shiftUp();
  else if (a === 'downshift' && car.gearbox.mode === 'sequential') car.gearbox.shiftDown(car.vx);
  else if (a === 'box') race?.toggleBox();
  else if (a === 'camera') toggleHorizon();
  else if (a === 'reset' && (!race || race.phase === 'racing')) session.reset();
}

/** Controller buttons: the wheel's actions where they overlap, plus back/prev/next for menus and the pit tyre choice. */
function padAction(a: ControllerAction): void {
  const driving = running && !paused && !menu.open && !settings.open && !carSetup.open && !results.open && !wheelSetup.open;
  if (a === 'back' || a === 'prev' || a === 'next') {
    if (driving) {
      if (pitMenu.shown && a !== 'back') pitMenu.cycle(a === 'next' ? 1 : -1);
    } else if (a === 'back') menuAction('pause');
    else wheelNav.move(a === 'next' ? 1 : -1);
    return;
  }
  wheelAction(a);
}

/** One line of controller help, shown when a drive starts. */
function padHint(): string {
  const l = controller.labels;
  return `${controller.name} · ${l.throttle} throttle · ${l.brake} brake · ${l.pause} pause · ${l.reset} reset`;
}

/** Wheel buttons in menus: paddles move, select presses, pause backs out. */
function menuAction(a: Action): void {
  if (wheelSetup.capturing) return; // learning a control: the press belongs to the learner
  if (a === 'select' && wheelSetup.open && wheelSetup.confirm()) return;
  if (a === 'upshift') wheelNav.move(1);
  else if (a === 'downshift') wheelNav.move(-1);
  else if (a === 'select') wheelNav.select();
  else if (a === 'pause') {
    // Back out of the top screen; the pause menu itself resumes.
    if (wheelSetup.open || carSetup.open || !menu.open) wheelNav.back();
    else keyboard.onPause?.();
  }
}

/** The roughest surface under either side, for force feedback. */
const FFB_SURFACE_ORDER: Surface[] = ['kerb', 'gravel', 'outside', 'grass', 'runoff-asphalt', 'track'];
function ffbSurface(): Surface {
  for (const s of FFB_SURFACE_ORDER) if (session.surfaceLeft === s || session.surfaceRight === s) return s;
  return session.surface;
}
let prevGear = 1;
let prevSteer = 0;
/** Seconds spent on the throttle without pulling away (wrong gear / neutral), for a HUD hint. */
let boggedTime = 0;

function gearHint(throttle: number, dt: number): void {
  const gb = session.car.gearbox;
  const bogged = gb.mode !== 'auto' && session.car.speed < 2 && throttle > 0.3 && (gb.gear > 2 || gb.gear === 0);
  boggedTime = bogged ? boggedTime + dt : 0;
  if (boggedTime > 1) {
    boggedTime = -3; // don't repeat for a few seconds
    hud.showNotice(gb.gear === 0 ? (gb.mode === 'hpattern' ? 'Neutral: clutch in and select a gear' : 'Neutral: upshift into 1st') : 'Shift down to 1st to pull away', 2.5);
  }
}
keyboard.onPause = () => {
  if (settings.open) settings.close();
  else if (menu.open) menu.close();
  else if (running) menu.show();
};
/** Leaving the game (app switch, lock screen, another window) pauses it, so you don't come back mid-corner. */
function pauseForInterruption(): void {
  // Forces off now: the render loop that normally does this stops in a hidden tab.
  wheel.setDriving(false);
  if (running && !menu.open && !settings.open) menu.show();
}
window.addEventListener('blur', pauseForInterruption);

let telemetry: Telemetry | undefined;
let fpsEl: HTMLElement | undefined;
if (dev) {
  fpsEl = document.createElement('div');
  fpsEl.className = 'dev-fps';
  document.getElementById('dev')!.appendChild(fpsEl);
  const devRoot = document.getElementById('dev') as HTMLElement;
  new DevPanel(devRoot, [
    { label: 'Horizon A/B', run: toggleHorizon },
    { label: 'Re-centre', run: recentre },
    { label: 'Restart lap', run: () => session.flyingStart() },
  ]);
  telemetry = new Telemetry(devRoot, [
    { label: 'steer', color: '#4fc3f7', min: -1, max: 1 },
    { label: 'roll', color: '#9575cd', min: -1.2, max: 1.2 },
    { label: 'thr', color: '#66bb6a', min: 0, max: 1 },
    { label: 'brk', color: '#ef5350', min: 0, max: 1 },
    { label: 'kmh', color: '#ffffff', min: 0, max: 350 },
    { label: 'slipR', color: '#ffa726', min: -0.3, max: 0.3 },
    { label: 'batt', color: '#ffee58', min: 0, max: 1 },
    { label: 'ffb', color: '#4dd0e1', min: -1, max: 1 },
  ]);
}

/** Sensors, audio, wake lock and fullscreen; needs a user gesture. False if motion access was refused. */
let sensorsStarted = false;
async function enterGame(): Promise<boolean> {
  // A controller steers, so skip the tilt permission: iOS only asks from a touch, and a controller
  // button doesn't count as one (it would come back denied and block the start).
  const permission = controller.connected ? 'unsupported' : await requestMotionPermission();
  if (permission === 'denied') {
    overlays.showMessage(PERMISSION_DENIED_HTML);
    return false;
  }
  sensor.start();
  unlockAudio();
  void requestWakeLock();
  void enterFullscreen();
  overlays.showStart(false);
  if (!sensorsStarted) {
    sensorsStarted = true;
    // If no orientation events arrive, explain (phones) or fall back to keyboard (desktop).
    setTimeout(() => {
      if (sensor.receiving) return;
      if (wheel.active) hud.showNotice(`${wheel.source!.name}${wheel.hasFfb ? ' · force feedback on' : ''} · E/Q shift · P pause`, 4);
      else if (controller.connected) return; // the start notice already explained the controller
      else if (matchMedia('(pointer: coarse)').matches) overlays.showMessage(NO_SENSOR_HTML);
      else hud.showNotice('Keyboard: arrows/WASD · C re-centre · H horizon · P pause · B box', 4);
    }, 1500);
  }
  return true;
}

overlays.onStart(async () => {
  if (!(await enterGame())) return;
  endRace();
  running = true;
  const at = dev ? Number(new URLSearchParams(location.search).get('at')) : NaN;
  if (Number.isFinite(at) && at > 0) {
    // Dev: start anywhere on the lap (?dev&at=<metres>).
    session.startAt(at, 120);
  } else if (!hasOnboarded() && !wheel.active && !controller.connected) {
    // A gentler start for the guided first drive (it teaches tilt and the touch pedals).
    session.flyingStart(120);
    onboarding.start();
  } else {
    session.flyingStart();
  }
  if (controller.connected && !wheel.active) {
    hud.showNotice(padHint(), 4);
    // Started from the controller: browsers only unlock sound from a click, tap or key.
    setTimeout(() => {
      if (running && audioCtx && audioCtx.state !== 'running') hud.showNotice('Click or tap the screen once to turn on the sound', 4);
    }, 4500);
  }
  recorder.startLap();
});
overlays.showStart(true);

let audioCtx: AudioContext | undefined;
let audio: CarAudio | undefined;
let rainAudio: RainAudio | undefined;
const audioState = idleAudioState();
const mgu = new MguMeter();
function unlockAudio(): void {
  // iOS only allows audio after a user gesture; create and resume it now.
  try {
    // Play through the ring/silent switch, like other games.
    configureAudioSession();
    audioCtx ??= new AudioContext();
    void audioCtx.resume();
    audio ??= new CarAudio(audioCtx);
    rainAudio ??= new RainAudio(audioCtx);
  } catch {
    // No Web Audio; the game still runs silently.
  }
}
// iOS can interrupt audio (calls, Siri) and only resumes from a gesture, so retry on touches.
document.getElementById('touch')!.addEventListener('pointerdown', () => {
  if (audioCtx?.state !== 'running') void audio?.resume();
});

let wakeLock: { release(): Promise<void> } | undefined;
async function requestWakeLock(): Promise<void> {
  try {
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    wakeLock = await nav.wakeLock?.request('screen');
  } catch {
    // Not supported or refused; the screen may dim during long sessions.
  }
}

async function enterFullscreen(): Promise<void> {
  try {
    if (document.documentElement.requestFullscreen) {
      await document.documentElement.requestFullscreen();
      const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
      await so.lock?.('landscape');
    }
  } catch {
    // iPhone Safari has no element fullscreen or orientation lock; the rotate overlay covers it.
  }
}

document.addEventListener('visibilitychange', () => {
  paused = document.hidden;
  if (document.hidden) pauseForInterruption();
  void (document.hidden ? audio?.suspend() : audio?.resume());
  if (!document.hidden && running && !wakeLock) void requestWakeLock();
  if (document.hidden) wakeLock = undefined;
});

let telemetryAcc = 0;
let fpsFrames = 0;
let fpsTime = 0;

const renderFrame = (draw = true): void => {
  const frameDt = captureDt || renderer.engine.getDeltaTime() / 1000;
  const car = session.car;

  const driving = running && !paused && !menu.open && !settings.open && !carSetup.open && !results.open && !wheelSetup.open;
  // The wheel's pause button works from menus too; everything else only while driving.
  const wheelState = wheel.poll();
  const padState = controller.poll();
  // A wheel steers when connected; otherwise a controller does (its buttons work either way).
  const padDrives = !!padState && !wheelState;
  if ((wheelState || padState) && !driving) {
    // Menus from the wheel or controller: highlight follows the top overlay; a steering flick also steps (not in wheel setup, where turning is the point).
    wheelNav.update();
    if (wheelState && !wheelSetup.open) wheelNav.steer(wheelState.steer);
    for (const a of wheelState?.pressed ?? []) wheelAction(a);
    for (const a of padState?.pressed ?? []) padAction(a);
  } else {
    wheelNav.reset();
  }
  if (!driving || !hapticsEnabled()) controller.rumble.stop(controller.pad);
  // Sound needs a user gesture; a controller press works where the page already has one (sticky activation).
  if (padState?.pressed.length && audioCtx && audioCtx.state !== 'running') void audio?.resume();
  syncDriveMode();
  wheel.setDriving(driving && wheel.hasFfb);
  // Wheel mode HUD: pedal bars instead of the re-centre hint.
  hud.setPedals(wheelState && driving ? { throttle: wheelState.throttle, brake: wheelState.brake, clutch: wheelState.clutch } : undefined);
  if (driving) {
    // Steering: the wheel if connected, else tilt when the sensor is live, else keyboard.
    let steer: number;
    let clutch = 0;
    if (!wheelState && sensor.receiving && !landscapeChosen) {
      // With the page in portrait (e.g. iOS orientation lock), pick the landscape the
      // phone is actually held in before steering takes its first reading as centre.
      landscapeChosen = true;
      frame.chooseFromGravity(sensor.downRight);
    }
    for (const a of padState?.pressed ?? []) padAction(a);
    if (wheelState) {
      steer = wheelState.steer;
      clutch = wheelState.clutch;
      for (const a of wheelState.pressed) wheelAction(a);
      if (car.gearbox.mode === 'hpattern' && wheelState.shifter !== undefined) car.gearbox.select(wheelState.shifter, clutch, car.vx);
    } else if (padDrives) {
      // The stick, unless the keyboard is steering harder (a pad left plugged in at a desk).
      const keys = keyboard.updateSteer(frameDt);
      steer = Math.abs(keys) > Math.abs(padState.steer) ? keys : padState.steer;
    } else if (sensor.receiving && sensor.latest) {
      const drivingStraight = car.speed > 25 && Math.abs(car.yawRate) < 0.02 && Math.abs(steering.output) < 0.1;
      steer = steering.update(sensor.latest.roll, frameDt, { drivingStraight });
    } else {
      steer = keyboard.updateSteer(frameDt);
    }
    const touch = pedals.state;
    const keys = keyboard.pedals;
    const input = {
      steer,
      throttle: Math.max(touch.throttle, keys.throttle, wheelState?.throttle ?? 0, padState?.throttle ?? 0),
      brake: Math.max(touch.brake, keys.brake, wheelState?.brake ?? 0, padState?.brake ?? 0),
      clutch,
    };

    let impactMax = 0;
    let substep = 0;
    const frameSpan = Math.min(Math.max(frameDt, 1e-3), 0.1);
    fixed.advance(frameDt, (dt) => {
      // Sim mode has no steering slew limit, so spread each frame's wheel movement
      // across the physics substeps instead of a 60 Hz staircase.
      substep++;
      const stepInput = autoDrive
        ? autoDrive.step(car, dt)
        : car.simMode
          ? { ...input, steer: prevSteer + (steer - prevSteer) * Math.min(1, (substep * dt) / frameSpan) }
          : input;
      if (race) {
        race.step(stepInput, dt);
        if (race.phase === 'racing' || race.phase === 'pit') {
          raceRecorder.record({ s: race.progress * track.length, t: race.raceTime, x: car.x, z: car.z, heading: car.heading });
        }
      } else {
        session.step(stepInput, dt);
        if (session.lapTime !== undefined) {
          recorder.record({ s: session.projection.s, t: session.lapTime, x: car.x, z: car.z, heading: car.heading });
        }
      }
      impactMax = Math.max(impactMax, session.impact);
      for (const e of session.events.splice(0)) {
        if (race) {
          if (e.kind === 'lap' && e.lapTime !== undefined && race.lapTimes.length > 0 && race.phase !== 'finished') {
            hud.showNotice(`Lap ${race.lapTimes.length}: ${formatTime(e.lapTime)}`, 2.5);
          } else if (e.kind === 'invalid') {
            vibrate(40);
          }
          continue;
        }
        if (e.kind === 'lap' && e.lapTime !== undefined) {
          const previous = recorder.bestTime;
          const pb = recorder.finishLap(e.lapTime, e.valid ?? false);
          const gain = pb && previous !== undefined ? ` (−${(previous - e.lapTime).toFixed(3)})` : '';
          hud.showNotice(`${e.valid ? (pb ? 'Personal best ' : 'Lap ') : 'Invalid lap '}${formatTime(e.lapTime)}${gain}`, 3);
          if (pb && recorder.best) {
            bestStored = { lapTime: e.lapTime, sectors: e.sectors ?? [], samples: recorder.best, date: new Date().toISOString() };
            void saveBest(pbKey, bestStored);
          }
        } else if (e.kind === 'invalid') {
          hud.showNotice('Lap invalidated: track limits', 2);
          vibrate(40);
        }
      }
      if (session.impact > 6) vibrate(Math.min(120, session.impact * 6));
    });
    prevSteer = steer;
    if (autoDrive && fakeWheel) autoDrive.frameEnd(fakeWheel, wheel.profile);
    if (wheelState) gearHint(input.throttle, frameDt);
    kerbRumble.update(session.surface === 'kerb', car.speed);
    if (padState && hapticsEnabled()) {
      const rough = ffbSurface();
      controller.rumble.update(
        controller.pad,
        {
          speed: car.speed,
          onKerb: rough === 'kerb',
          offTrack: rough === 'grass' || rough === 'gravel' || rough === 'outside',
          impact: impactMax,
          frontLock: car.frontLock,
          rearSpin: car.rearSpin,
          absCut: car.absCut,
          tcCut: car.tcCut,
        },
        frameDt,
      );
    }
    if (wheel.hasFfb) {
      wheel.updateFfb(
        {
          aligningTorque: car.aligningTorque,
          speed: car.speed,
          surface: ffbSurface(),
          impact: impactMax,
          impactSide: session.impactSide,
          frontLock: car.frontLock,
          rearSpin: car.rearSpin,
          shifted: car.gearbox.gear !== prevGear,
        },
        frameDt,
      );
      wheel.updateLeds(car.gearbox.shiftLight, car.gearbox.limiter);
    }
    prevGear = car.gearbox.gear;
    if (rainAudio) {
      rainAudio.setMuted(false);
      rainAudio.update(race?.weather.rain ?? 0, race?.weather.wetness ?? 0, car.speed, frameDt);
    }
    if (audio) {
      audio.setMuted(false);
      audio.update(fillAudioState(audioState, session, input.throttle, mgu.update(car.pu.charge, frameDt), impactMax), frameDt);
    }
    onboarding.update({ ...input, recentred }, frameDt);
    recentred = false;
    if (race) {
      raceHud.radio(race.radio.splice(0));
      if (race.phase === 'finished') {
        if (!raceFinishedAt) {
          raceFinishedAt = performance.now();
          // Keep the race's path for the ghost before anything else records.
          raceRecorder.finishLap(race.total, true);
        } else if (performance.now() - raceFinishedAt > 2500 && !results.open) {
          finishRace(race);
        }
      }
    }
  }

  // Silence the car whenever it isn't being driven (start screen, pause menu, hidden page).
  if (audio && !driving) audio.setMuted(true);
  if (!driving) rainAudio?.setMuted(true);

  // Ground height and road pitch under the car (zero on flat tracks).
  const under = track.frameAt(session.projection.s);
  const alignment = Math.sin(car.heading) * under.tx + Math.cos(car.heading) * under.tz;
  renderer.updateCar(car, under.y, Math.atan(under.slope * alignment));
  const wx = race?.weather;
  weatherFx.update({
    rain: wx?.rain ?? 0,
    wetness: wx?.wetness ?? 0,
    // A dry line appears once the rain has stopped and the water drains off the racing line.
    dryLine: wx && wx.rain < 0.05 ? Math.min(1, Math.max(0, (0.35 - wx.wetness) / 0.3)) : 0,
    carX: car.x,
    carZ: car.z,
    carY: under.y,
    heading: car.heading,
    speed: car.speed,
    dt: frameDt,
  });
  // Ghost of the best lap (or best race), faded out when it's right on top of the player's car.
  const pose = !ghostOn
    ? undefined
    : race
      ? race.phase === 'racing' || race.phase === 'pit'
        ? raceBest.bestPoseAt(race.raceTime)
        : undefined
      : session.lapTime !== undefined
        ? recorder.bestPoseAt(session.lapTime)
        : undefined;
  const ghostPose = pose ? { ...pose, y: track.heightNear(pose.x, pose.z, session.projection.index).y } : undefined;
  const ghostGap = ghostPose ? Math.hypot(ghostPose.x - car.x, ghostPose.z - car.z) : 0;
  renderer.updateGhost(ghostPose, 0.35 * Math.min(1, Math.max(0, (ghostGap - 4) / 8)));
  renderer.updateCamera(car, sensor.receiving && !padDrives && !wheelState ? steering.relativeRoll : 0, horizon);

  const best = !race && session.lapTime !== undefined ? recorder.bestTimeAt(session.projection.s) : undefined;
  const raceLapBest = race && race.lapTimes.length ? Math.min(...race.lapTimes) : undefined;
  hud.update({
    speedKmh: car.speed * 3.6,
    gear: car.gearbox.label,
    shiftLight: car.gearbox.shiftLight,
    steer: (wheel.active ? wheel.state?.steer : padDrives ? padState.steer : steering.output) || 0,
    steerVisible: dev || session.lapNumber < 3,
    lapTime: session.lapTime,
    lastLap: session.lastLap,
    bestLap: race ? raceLapBest : session.bestLap,
    delta: best !== undefined && session.lapTime !== undefined ? session.lapTime - best : undefined,
    lapValid: session.lapValid,
    sectors: session.sectorTimes,
    bestSectors: session.bestSectors,
    straightMode: car.aero.blend > 0.3,
    carX: car.x,
    carZ: car.z,
    heading: car.heading,
    battery: car.pu.chargeFraction,
    deploying: car.mguK,
  });
  hud.setAeroLabel(race?.weather.lowGrip ? 'FRONT WING' : 'STRAIGHT MODE');
  if (race) updateRaceHud(race);

  if (telemetry) {
    telemetryAcc += frameDt;
    if (telemetryAcc >= 1 / telemetry.rate) {
      telemetryAcc = 0;
      telemetry.push([
        wheel.active ? (wheel.state?.steer ?? 0) : padDrives ? padState.steer : steering.output,
        sensor.latest?.roll ?? 0,
        Math.max(pedals.state.throttle, keyboard.pedals.throttle, wheel.state?.throttle ?? 0, padState?.throttle ?? 0),
        Math.max(pedals.state.brake, keyboard.pedals.brake, wheel.state?.brake ?? 0, padState?.brake ?? 0),
        car.speed * 3.6,
        car.slipRear,
        car.pu.chargeFraction,
        wheel.ffb.lastTotal,
      ]);
      telemetry.draw();
    }
  }

  inputOverlay?.update(car.gearbox.label, car.speed * 3.6);
  // Capture runs at its own pace: keep the resolution and quality fixed.
  if (!captureDt) {
    renderer.adaptResolution(frameDt);
    if (driving) quality.observe(frameDt, renderer.resolutionScale, renderer.atMinScale);
  }
  if (fpsEl) {
    fpsFrames++;
    fpsTime += frameDt;
    if (fpsTime >= 0.5) {
      fpsEl.textContent = `${Math.round(fpsFrames / fpsTime)} fps · res ${Math.round(renderer.resolutionScale * 100)}%`;
      fpsFrames = 0;
      fpsTime = 0;
    }
  }
  // Capture can fast-forward (simulate without drawing) to split a recording across browsers.
  if (draw) renderer.scene.render();
};
if (captureDt) (window as unknown as { renderFrame: (draw?: boolean) => void }).renderFrame = renderFrame;
else renderer.engine.runRenderLoop(renderFrame);
